import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessagesSquare, Send, Loader2, ArrowLeft, ShieldCheck } from 'lucide-react';
import PoPShell from './PoPShell';
import { usePoPAuth } from '../../context/PoPAuthContext';
import {
  popListThreads, popGetThread, popSendMessage, popCreateThread, roleLabel,
} from '../../lib/popApi';

const relativeTime = (iso) => {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

const PoPMessages = () => {
  const { user, loading } = usePoPAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const feedRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) navigate('/pop', { replace: true });
  }, [user, loading, navigate]);

  const loadThreads = async () => {
    setBusy(true);
    try {
      const r = await popListThreads();
      setThreads(r.results || []);
      // Auto-open if navigated with state {peerUserId, subject, contextType, contextId, initialMessage}
      const cs = location.state;
      if (cs && cs.peerUserId && (r.results || []).length === 0) {
        try {
          const c = await popCreateThread({
            peer_user_id: cs.peerUserId,
            subject: cs.subject || 'Conversation',
            context_type: cs.contextType || 'direct',
            context_id: cs.contextId || '',
            initial_message: cs.initialMessage || '',
          });
          setSelected(c.thread);
          await openThread(c.thread.id);
          await loadThreads();
        } catch (e) {
          setErr((e && e.response && e.response.data && e.response.data.detail) || 'Unable to open thread');
        }
      }
    } catch (_e) {
      setThreads([]);
    } finally { setBusy(false); }
  };

  const openThread = async (id) => {
    setErr('');
    try {
      const r = await popGetThread(id);
      setSelected(r.thread);
      setMessages(r.messages || []);
      setTimeout(() => { if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight; }, 50);
    } catch (e) {
      setErr((e && e.response && e.response.data && e.response.data.detail) || 'Could not open thread');
    }
  };

  useEffect(() => { if (user) loadThreads(); /* eslint-disable-next-line */ }, [user]);

  const send = async () => {
    if (!selected || !draft.trim()) return;
    setSending(true);
    try {
      const m = await popSendMessage(selected.id, draft.trim());
      setMessages((prev) => [...prev, m.message]);
      setDraft('');
      setTimeout(() => { if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight; }, 30);
      // Update thread list preview
      setThreads((prev) => prev.map((t) => t.id === selected.id ? { ...t, last_message_preview: m.message.body.slice(0, 120), updated_at: m.message.created_at } : t));
    } catch (e) {
      setErr((e && e.response && e.response.data && e.response.data.detail) || 'Could not send');
    } finally { setSending(false); }
  };

  if (loading) return (<PoPShell><div className="flex items-center justify-center py-24 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div></PoPShell>);

  return (
    <PoPShell activeTab="messages">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">MESSAGES</div>
        <h1 className="font-serif text-3xl sm:text-4xl">Conversations</h1>
        <p className="text-sm text-white/60 mt-1 max-w-2xl">
          <ShieldCheck className="w-3.5 h-3.5 text-amber-300 inline mr-1 -mt-0.5" />
          Permission-based — messaging is unlocked once you share a context (opportunity, invitation, or verified match).
        </p>
      </div>

      <div className="grid lg:grid-cols-[320px,1fr] gap-4 h-[600px]">
        {/* Threads list */}
        <div className="rounded-xl border border-white/10 bg-slate-900/40 overflow-hidden flex flex-col">
          <div className="px-4 py-2.5 border-b border-white/10 text-[10px] tracking-[0.3em] font-bold text-white/50">THREADS · {threads.length}</div>
          <div className="flex-1 overflow-y-auto">
            {busy ? (
              <div className="flex items-center justify-center py-10 text-white/50 text-xs"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…</div>
            ) : threads.length === 0 ? (
              <div className="p-6 text-xs text-white/50 italic text-center">
                No conversations yet. Reach out via an opportunity or accept an invitation to start a thread.
              </div>
            ) : threads.map((t) => (
              <button key={t.id}
                onClick={() => openThread(t.id)}
                data-testid={`thread-${t.id}`}
                className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${
                  selected?.id === t.id ? 'bg-white/[0.04]' : ''
                }`}>
                <div className="flex items-center gap-2.5">
                  {t.peer?.picture
                    ? <img src={t.peer.picture} alt="" className="w-8 h-8 rounded-full ring-1 ring-white/20" />
                    : <div className="w-8 h-8 rounded-full bg-amber-300/20 text-amber-200 flex items-center justify-center text-xs font-bold">{(t.peer?.name || '?').slice(0,1).toUpperCase()}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-medium truncate flex items-center gap-1.5">
                      {t.peer?.name || 'Conversation'}
                      {t.peer?.verification_status === 'verified' && <ShieldCheck className="w-3 h-3 text-emerald-400" />}
                    </div>
                    <div className="text-[10px] text-amber-300 uppercase tracking-wider">{roleLabel(t.peer?.role)}</div>
                    <div className="text-[11px] text-white/50 truncate mt-0.5">{t.last_message_preview || t.subject}</div>
                  </div>
                  <div className="text-[9px] text-white/40 whitespace-nowrap">{relativeTime(t.updated_at)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Message viewer */}
        <div className="rounded-xl border border-white/10 bg-slate-900/40 overflow-hidden flex flex-col">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-white/40 text-sm">
              <div className="text-center">
                <MessagesSquare className="w-8 h-8 text-white/20 mx-auto mb-2" />
                Select a thread to view messages
              </div>
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2.5">
                <button className="lg:hidden text-white/60" onClick={() => setSelected(null)}><ArrowLeft className="w-4 h-4" /></button>
                <div>
                  <div className="text-sm font-serif font-semibold text-white">{selected.subject}</div>
                  <div className="text-[10px] text-white/50 uppercase tracking-wider">{selected.context_type}</div>
                </div>
              </div>
              <div ref={feedRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="messages-feed">
                {messages.map((m) => {
                  const mine = m.sender_user_id === user.user_id;
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl text-sm ${
                        mine ? 'bg-amber-300 text-slate-900 rounded-br-md' : 'bg-white/10 text-white rounded-bl-md'
                      }`}>
                        <div>{m.body}</div>
                        <div className={`text-[9px] mt-1 ${mine ? 'text-slate-600' : 'text-white/40'}`}>{relativeTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && <div className="text-xs text-white/40 italic text-center">Send the first message…</div>}
              </div>
              <div className="border-t border-white/10 p-3 flex items-center gap-2">
                <input value={draft} onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
                  placeholder="Write a message…"
                  data-testid="msg-input"
                  className="flex-1 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-300" />
                <button onClick={send} disabled={sending || !draft.trim()}
                  data-testid="msg-send"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-amber-300 hover:bg-amber-200 disabled:opacity-40 text-slate-900 text-sm font-semibold">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {err && <div className="mt-3 text-xs text-rose-300">{err}</div>}
    </PoPShell>
  );
};

export default PoPMessages;
